/**
 * The rail's token tree. Clicking a token's row folds or unfolds its pages
 * (lib/sections, led by its saved default view when that is not the
 * overview). The token being viewed is always open, with its page marked, and
 * cannot be folded; any other token is open only while the reader has opened
 * it by hand (remembered per reader). So moving to another token opens that
 * one and folds the one left behind, unless the reader had opened it.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Link, NavLink, useLocation } from "react-router";
import type { TokenJob, View } from "@/api/types";
import { fmtCompact, fmtPrice } from "@/lib/format";
import { routes, syncState, tokenLabel } from "@/lib/jobs";
import { TOKEN_SECTIONS } from "@/lib/sections";
import { defaultViewKey, tokenTree, treeKey, type TreeItem } from "@/lib/tree";
import { useUi } from "@/state/ui";
import { useDefaultViews } from "@/views/family/viewsHooks";
import { useRailFlyout } from "./railFlyoutContext";
import { fullCls, guideCls, linkCls, rowCls, truncCls } from "./railStyles";

const PINNED_TITLE = "the token you are viewing stays open";

/**
 * One tree of token entries (role="tree"). Arrow keys move between visible
 * items (lib/tree), → / ← open and close a token, so do Enter and Space on a
 * token, Enter on a page follows its link; one item at a time takes Tab
 * (roving tabindex), the page being viewed by default.
 */
export function TokenTree({
  label,
  jobs,
  activeId,
}: {
  label: string;
  jobs: TokenJob[];
  activeId: string | undefined;
}) {
  const railTree = useUi((s) => s.railTree);
  const setOpen = useUi((s) => s.setRailTokenOpen);
  const location = useLocation();
  const ref = useRef<HTMLDivElement>(null);
  const [tabKey, setTabKey] = useState<string | null>(null);
  const defaults = useDefaultViews();
  if (jobs.length === 0) return null;
  // a saved default view that is just the overview would list the same page twice
  const shownDefault = (j: TokenJob) => {
    const v = defaults.get(j.id);
    return v && v.url.replace(/\/$/, "") !== routes.overview(j.chain, j.token) ? v : undefined;
  };
  const withDefault = new Set(jobs.filter((j) => shownDefault(j)).map((j) => j.id));
  const tree = tokenTree(jobs, TOKEN_SECTIONS, activeId, railTree, location.pathname, withDefault);
  // the item Tab lands on: the last one focused if it is still shown, else the page being viewed, else the first
  const stop = tabKey && tree.keys.includes(tabKey) ? tabKey : (tree.here ?? tree.keys[0]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const nodes = [...(ref.current?.querySelectorAll<HTMLElement>("[data-tree-key]") ?? [])];
    const items: TreeItem[] = nodes.map((n) => ({
      level: n.getAttribute("aria-level") === "2" ? 2 : 1,
      expandable: n.getAttribute("aria-level") !== "2",
      expanded: n.getAttribute("aria-expanded") === "true",
      pinned: n.dataset.pinned !== undefined,
    }));
    const move = treeKey(items, nodes.indexOf(e.target as HTMLElement), e.key);
    if (!move) return;
    e.preventDefault();
    if (move.toggle !== undefined) {
      const id = nodes[move.toggle]!.dataset.treeKey!;
      setOpen(id, !items[move.toggle]!.expanded);
    }
    if (move.nudge !== undefined) nudge(nodes[move.nudge]!);
    if (move.focus !== undefined) nodes[move.focus]!.focus();
  };

  return (
    <div
      ref={ref}
      role="tree"
      aria-label={label}
      onKeyDown={onKeyDown}
      onFocus={(e) => {
        const key = (e.target as HTMLElement).dataset.treeKey;
        if (key) setTabKey(key);
      }}
    >
      {jobs.map((j) => (
        <TokenEntry
          key={j.id}
          job={j}
          active={j.id === activeId}
          open={tree.open[j.id] ?? false}
          onToggle={() => setOpen(j.id, !tree.open[j.id])}
          defaultView={shownDefault(j)}
          tabStop={stop}
        />
      ))}
    </div>
  );
}

/**
 * Twitch a pinned token's arrow: it cannot fold, and this says so without a
 * message. Skipped when the reader asks for reduced motion.
 */
function nudge(row: HTMLElement) {
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
  row
    .querySelector<HTMLElement>("[data-twisty]")
    ?.animate?.([{ transform: "none" }, { transform: "translateY(2px)" }, { transform: "none" }], {
      duration: 250,
      easing: "ease-out",
    });
}

function TokenEntry({
  job,
  active,
  open,
  onToggle,
  defaultView,
  tabStop,
}: {
  job: TokenJob;
  /** the token being viewed: always open, its header does not fold it */
  active: boolean;
  open: boolean;
  onToggle: () => void;
  defaultView: View | undefined;
  tabStop: string | undefined;
}) {
  const { chain, token } = job;
  const location = useLocation();
  const inherit = new URLSearchParams(location.search).get("inherit_transfers");
  const basisSearch = active && (inherit === "1" || inherit === "true") ? "?inherit_transfers=1" : "";
  const sync = syncState(job.tracking);
  const defaultKey = defaultViewKey(job.id);
  const onDefault = defaultView !== undefined && location.pathname + location.search === defaultView.url;
  // a closed token lists its pages in the hover flyout (RailFlyout), where there is one
  const fly = useRailFlyout();
  const flies = fly.enabled && !open;
  const flyOn = fly.shownId === job.id;
  const closeFly = fly.close;
  useEffect(() => {
    if (open && flyOn) closeFly();
  }, [open, flyOn, closeFly]);
  const summary = `${tokenLabel(job)} · ${fmtCompact(job.counts.actions)} actions${sync.syncing ? "" : ` · ${sync.detail}`}`;
  return (
    <div role="none">
      {/* the whole row folds and unfolds the token (Enter / Space via TokenTree); its pages are the links */}
      <div
        // the token being viewed reads as "you are in here" (fg, bolder symbol) without a fill: its page carries the mark
        className={`${rowCls} select-none ${active ? "cursor-default" : "cursor-pointer"} ${active || flyOn ? "text-fg" : "text-fg-muted"} ${flyOn ? "bg-bg-hover" : ""}`}
        role="treeitem"
        aria-level={1}
        aria-expanded={open}
        data-tree-key={job.id}
        data-pinned={active ? "" : undefined}
        tabIndex={tabStop === job.id ? 0 : -1}
        title={active ? `${summary} · ${PINNED_TITLE}` : summary}
        onClick={(e) => {
          fly.close();
          if (active) nudge(e.currentTarget);
          else onToggle();
        }}
        onMouseEnter={flies ? (e) => fly.enter({ job, defaultView, row: e.currentTarget }) : undefined}
        onMouseLeave={flies ? fly.leave : undefined}
      >
        <span
          className={`inline-block w-2.5 flex-none text-fg-faint ${active ? "opacity-45" : ""}`}
          aria-hidden
          data-twisty
        >
          {open ? "▾" : "▸"}
        </span>
        <span className={`flex-none ${active ? "font-bold" : "font-semibold"}`}>{job.symbol}</span>
        <span className={`${truncCls} text-fg-faint`}>
          {chain} · {token.slice(-4)}
        </span>
        {/* a stopped job's last price is stale, so its state takes the price's place; neither ever wraps */}
        {sync.syncing ? (
          job.mark && (
            <span className="num ml-auto flex-none pl-1.5 text-fg-muted">{fmtPrice(job.mark.price_usd)}</span>
          )
        ) : (
          <span
            className={`ml-auto flex-none pl-1.5 ${sync.tone === "danger" ? "text-danger" : "text-warn"}`}
            data-testid="rail-sync"
          >
            {sync.label}
          </span>
        )}
      </div>
      {open && (
        <div role="group" className={guideCls}>
          {defaultView && (
            // a Link, not a NavLink: the view's query string decides whether it is the page being viewed
            <Link
              to={defaultView.url}
              className={`${linkCls({ isActive: onDefault }, "guide")} ${fullCls}`}
              aria-current={onDefault ? "page" : undefined}
              role="treeitem"
              aria-level={2}
              data-tree-key={defaultKey}
              tabIndex={tabStop === defaultKey ? 0 : -1}
              title={`your default view of ${job.symbol}: ${defaultView.url}`}
            >
              <span className="min-w-0 truncate">{defaultView.name}</span>
              <span className="flex-none font-normal text-fg-faint">· default</span>
            </Link>
          )}
          {TOKEN_SECTIONS.map((s) => (
            <NavLink
              key={s.id}
              to={s.path(chain, token) + (s.keepsBasis ? basisSearch : "")}
              end={s.id === ""}
              className={(state) => linkCls(state, "guide")}
              role="treeitem"
              aria-level={2}
              data-tree-key={`${job.id}/${s.id}`}
              tabIndex={tabStop === `${job.id}/${s.id}` ? 0 : -1}
            >
              {s.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}
